import { useEffect } from "react";
import { Link as RouterLink, Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { useI18n, detectLang, LANG_RE } from "@/i18n/I18nContext";
import CookieConsent from "@/visitor/CookieConsent";

// Link that automatically prefixes absolute visitor paths with the current language.
export function Link({ to, ...rest }) {
  const { prefix } = useI18n();
  const target = typeof to === "string" && to.startsWith("/") && !LANG_RE.test(to) && !to.startsWith("/admin") ? `${prefix}${to === "/" ? "" : to}` || "/" : to;
  return <RouterLink to={target} {...rest} />;
}

// Redirects prefix-less visitor URLs (QR codes, old links) to the right language.
export function RedirectToLang() {
  const { languages, loaded } = useI18n();
  const location = useLocation();
  if (!loaded) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)] text-xs tracking-[0.3em] uppercase">Loading…</div>;
  const code = detectLang(languages.map((l) => l.code));
  return <Navigate to={`/${code}${location.pathname === "/" ? "" : location.pathname}${location.search}`} replace />;
}

function Hreflang() {
  const { languages } = useI18n();
  const location = useLocation();
  useEffect(() => {
    document.querySelectorAll('link[data-hreflang]').forEach((n) => n.remove());
    const rest = location.pathname.replace(LANG_RE, "");
    const add = (hreflang, href) => { const l = document.createElement("link"); l.rel = "alternate"; l.hreflang = hreflang; l.href = href; l.dataset.hreflang = "1"; document.head.appendChild(l); };
    languages.forEach((l) => add(l.code, `${window.location.origin}/${l.code}${rest}`));
    add("x-default", `${window.location.origin}/en${rest}`);
    document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
    const c = document.createElement("link"); c.rel = "canonical"; c.href = `${window.location.origin}${location.pathname}`; document.head.appendChild(c);
    document.documentElement.lang = location.pathname.match(LANG_RE)?.[1] || "en";
  }, [languages, location.pathname]);
  return null;
}

// Validates the :lang URL segment and keeps the i18n state in sync with it.
export function LangGate() {
  const { lang: param } = useParams();
  const { languages, loaded, lang, syncLangFromUrl } = useI18n();
  const location = useLocation();
  const valid = loaded && languages.some((l) => l.code === param);
  useEffect(() => { if (valid && param !== lang) syncLangFromUrl(param); }, [valid, param, lang, syncLangFromUrl]);
  if (!loaded) return <div className="min-h-screen flex items-center justify-center text-[var(--muted)] text-xs tracking-[0.3em] uppercase">Loading…</div>;
  if (!valid) {
    const code = detectLang(languages.map((l) => l.code));
    const rest = location.pathname.replace(LANG_RE, "");
    return <Navigate to={`/${code}${rest}${location.search}`} replace />;
  }
  return <><Hreflang /><CookieConsent /><Outlet /></>;
}
